import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * AuctionHistoryTab - View past auction results with filtering and pagination
 * Shows closed/cancelled auctions with final prices, winners, and outcomes
 */
import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FaSync, FaHistory, FaFilter, FaChevronLeft, FaChevronRight, FaTrophy, FaTimesCircle, FaBan, FaGavel, } from 'react-icons/fa';
import { auctionService } from '../../../services/auctionService';
// ── Styled Components ────────────────────────────────────────
const Container = styled.div `
  padding: 0;
`;
const HeaderActions = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;
const ActionGroup = styled.div `
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`;
const Button = styled.button `
  padding: 0.65rem 1.25rem;
  border-radius: 6px;
  border: 1px solid ${props => props.$primary ? 'var(--accent-gold)' : 'var(--border-color)'};
  background: ${props => props.$primary ? 'var(--accent-gold)' : 'var(--bg-secondary)'};
  color: ${props => props.$primary ? 'white' : 'var(--text-primary)'};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.85rem;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
const FilterGroup = styled.div `
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;
const FilterLabel = styled.span `
  font-size: 0.8rem;
  color: var(--text-tertiary);
  white-space: nowrap;
`;
const FilterInput = styled.input `
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.85rem;
  background: var(--bg-secondary);
  color: var(--text-primary);
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--accent-gold);
  }
`;
const FilterSelect = styled.select `
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.85rem;
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;

  option {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }

  &:focus {
    outline: none;
    border-color: var(--accent-gold);
  }
`;
const TableContainer = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow-x: auto;
  max-width: 100%;
`;
const Table = styled.table `
  width: 100%;
  border-collapse: collapse;
  min-width: 950px;
`;
const Th = styled.th `
  text-align: left;
  padding: 1rem;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--text-primary);
  white-space: nowrap;
`;
const Td = styled.td `
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  font-size: 0.875rem;
  color: var(--text-primary);
  vertical-align: middle;
`;
const Tr = styled.tr `
  transition: background 0.15s;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};

  &:hover {
    background: var(--bg-tertiary);
  }

  &:last-child td {
    border-bottom: none;
  }
`;
const TypeBadge = styled.span `
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.$type === 'supervised' ? 'rgba(139, 92, 246, 0.15)' : 'rgba(6, 182, 212, 0.15)'};
  color: ${props => props.$type === 'supervised' ? '#8b5cf6' : '#06b6d4'};
`;
const PriceCell = styled.span `
  font-weight: 600;
  color: var(--accent-gold);
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.9rem;
`;
const ResultBadge = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;

  background: ${props => {
    switch (props.$result) {
        case 'sold': return 'rgba(16, 185, 129, 0.15)';
        case 'no_sale': return 'rgba(245, 158, 11, 0.15)';
        case 'cancelled': return 'rgba(239, 68, 68, 0.15)';
    }
}};
  color: ${props => {
    switch (props.$result) {
        case 'sold': return '#10b981';
        case 'no_sale': return '#f59e0b';
        case 'cancelled': return '#ef4444';
    }
}};
`;
const WinnerCell = styled.div `
  font-size: 0.85rem;
  color: var(--text-primary);

  .anonymous-id {
    font-weight: 600;
    color: #8b5cf6;
  }

  .no-winner {
    color: var(--text-tertiary);
    font-style: italic;
  }
`;
const PaginationContainer = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.25rem;
  padding: 0 0.5rem;
`;
const PaginationInfo = styled.span `
  font-size: 0.85rem;
  color: var(--text-secondary);
`;
const PaginationButtons = styled.div `
  display: flex;
  gap: 0.5rem;
`;
const PageButton = styled.button `
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid ${props => props.$active ? 'var(--accent-gold)' : 'var(--border-color)'};
  background: ${props => props.$active ? 'var(--accent-gold)' : 'var(--bg-secondary)'};
  color: ${props => props.$active ? 'white' : 'var(--text-primary)'};
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    border-color: var(--accent-gold);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;
const EmptyState = styled.div `
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-tertiary);

  svg {
    font-size: 4rem;
    color: var(--accent-gold);
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  h3 {
    color: var(--text-primary);
    margin: 0 0 0.5rem 0;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;
const LoadingState = styled.div `
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
`;
// Detail modal
const DetailOverlay = styled.div `
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
const DetailModal = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;
const DetailHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;

  h3 {
    margin: 0;
    font-size: 1.1rem;
    color: var(--text-primary);
  }
`;
const CloseButton = styled.button `
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-tertiary);
  padding: 0;
  line-height: 1;

  &:hover {
    color: var(--text-primary);
  }
`;
const DetailGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;
const DetailItem = styled.div `
  padding: 0.75rem 1rem;
  background: var(--bg-tertiary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
`;
const DetailLabel = styled.div `
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-tertiary);
  margin-bottom: 0.25rem;
`;
const DetailValue = styled.div `
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
`;
// ── Component ────────────────────────────────────────────────
const AuctionHistoryTab = () => {
    const [loading, setLoading] = useState(true);
    const [auctions, setAuctions] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const pageSize = 15;
    // Filters
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [resultFilter, setResultFilter] = useState('');
    // Detail modal
    const [selectedAuction, setSelectedAuction] = useState(null);
    const fetchHistory = useCallback(async () => {
        setLoading(true);
        try {
            const statusParam = resultFilter || 'closed,cancelled';
            const result = await auctionService.listAuctions({
                status: statusParam,
                page,
                pageSize,
            });
            if (result.success && result.data) {
                let filtered = result.data.auctions;
                // Client-side date filtering
                if (dateFrom) {
                    const from = new Date(dateFrom);
                    filtered = filtered.filter(a => {
                        const endDate = a.actualEndTime ? new Date(a.actualEndTime) : new Date(a.updatedAt);
                        return endDate >= from;
                    });
                }
                if (dateTo) {
                    const to = new Date(dateTo);
                    to.setHours(23, 59, 59);
                    filtered = filtered.filter(a => {
                        const endDate = a.actualEndTime ? new Date(a.actualEndTime) : new Date(a.updatedAt);
                        return endDate <= to;
                    });
                }
                setAuctions(filtered);
                setTotal(result.data.pagination.total);
                setTotalPages(result.data.pagination.totalPages);
            }
        }
        catch {
            // Silently fail
        }
        finally {
            setLoading(false);
        }
    }, [page, resultFilter, dateFrom, dateTo]);
    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);
    const getResult = (auction) => {
        if (auction.status === 'cancelled')
            return 'cancelled';
        if (auction.winnerId && auction.finalPrice)
            return 'sold';
        return 'no_sale';
    };
    const getResultIcon = (result) => {
        switch (result) {
            case 'sold': return _jsx(FaTrophy, {});
            case 'no_sale': return _jsx(FaBan, {});
            case 'cancelled': return _jsx(FaTimesCircle, {});
        }
    };
    const getResultLabel = (result) => {
        switch (result) {
            case 'sold': return 'Sold';
            case 'no_sale': return 'No Sale';
            case 'cancelled': return 'Cancelled';
        }
    };
    const formatDuration = (auction) => {
        const start = new Date(auction.scheduledStartTime);
        const end = auction.actualEndTime ? new Date(auction.actualEndTime) : new Date(auction.scheduledEndTime);
        const diff = end.getTime() - start.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 24)
            return `${Math.floor(hours / 24)}d ${hours % 24}h`;
        if (hours > 0)
            return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };
    const handleViewDetails = (auction) => {
        setSelectedAuction(auction);
    };
    if (loading && auctions.length === 0) {
        return (_jsx(Container, { children: _jsx(LoadingState, { children: "Loading auction history..." }) }));
    }
    return (_jsxs(Container, { children: [_jsxs(HeaderActions, { children: [_jsx(ActionGroup, { children: _jsxs(Button, { "$primary": true, onClick: fetchHistory, disabled: loading, children: [_jsx(FaSync, {}), " Refresh"] }) }), _jsxs(ActionGroup, { children: [_jsx(FaFilter, { style: { color: 'var(--text-tertiary)', fontSize: '0.8rem' } }), _jsxs(FilterGroup, { children: [_jsx(FilterLabel, { children: "From:" }), _jsx(FilterInput, { type: "date", value: dateFrom, onChange: e => { setDateFrom(e.target.value); setPage(1); } })] }), _jsxs(FilterGroup, { children: [_jsx(FilterLabel, { children: "To:" }), _jsx(FilterInput, { type: "date", value: dateTo, onChange: e => { setDateTo(e.target.value); setPage(1); } })] }), _jsxs(FilterSelect, { value: resultFilter, onChange: e => { setResultFilter(e.target.value); setPage(1); }, children: [_jsx("option", { value: "", children: "All Results" }), _jsx("option", { value: "closed", children: "Closed (Sold/No Sale)" }), _jsx("option", { value: "cancelled", children: "Cancelled" })] })] })] }), auctions.length > 0 ? (_jsxs(_Fragment, { children: [_jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Diamond" }), _jsx(Th, { children: "Type" }), _jsx(Th, { children: "Final Price" }), _jsx(Th, { children: "Winner" }), _jsx(Th, { children: "Bids" }), _jsx(Th, { children: "Duration" }), _jsx(Th, { children: "Result" }), _jsx(Th, { style: { textAlign: 'right' }, children: "Date" })] }) }), _jsx("tbody", { children: auctions.map(auction => {
                                        const result = getResult(auction);
                                        return (_jsxs(Tr, { "$clickable": true, onClick: () => handleViewDetails(auction), children: [_jsxs(Td, { children: [_jsxs("strong", { children: [auction.diamondSnapshot.caratWeight, "ct", ' ', auction.diamondSnapshot.shape, ' ', auction.diamondSnapshot.color, ' ', auction.diamondSnapshot.clarity] }), _jsx("div", { style: { fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '0.2rem' }, children: auction.diamondStockNumber || auction.diamondSnapshot.certificateNumber || '-' })] }), _jsx(Td, { children: _jsx(TypeBadge, { "$type": auction.auctionType, children: auction.auctionType }) }), _jsx(Td, { children: _jsx(PriceCell, { children: auction.finalPrice
                                                            ? `$${auction.finalPrice.toLocaleString()}`
                                                            : '-' }) }), _jsx(Td, { children: _jsx(WinnerCell, { children: auction.winnerId ? (_jsx("span", { className: "anonymous-id", children: "Winner" })) : (_jsx("span", { className: "no-winner", children: "None" })) }) }), _jsx(Td, { children: auction.bidCount }), _jsx(Td, { style: { fontSize: '0.85rem', color: 'var(--text-secondary)' }, children: formatDuration(auction) }), _jsx(Td, { children: _jsxs(ResultBadge, { "$result": result, children: [getResultIcon(result), " ", getResultLabel(result)] }) }), _jsx(Td, { style: { textAlign: 'right', fontSize: '0.8rem', color: 'var(--text-secondary)' }, children: auction.actualEndTime
                                                        ? new Date(auction.actualEndTime).toLocaleDateString()
                                                        : new Date(auction.updatedAt).toLocaleDateString() })] }, auction.id));
                                    }) })] }) }), _jsxs(PaginationContainer, { children: [_jsxs(PaginationInfo, { children: ["Showing ", (page - 1) * pageSize + 1, "-", Math.min(page * pageSize, total), " of ", total, " auctions"] }), _jsxs(PaginationButtons, { children: [_jsx(PageButton, { disabled: page <= 1, onClick: () => setPage(p => Math.max(1, p - 1)), children: _jsx(FaChevronLeft, {}) }), Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        }
                                        else if (page <= 3) {
                                            pageNum = i + 1;
                                        }
                                        else if (page >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        }
                                        else {
                                            pageNum = page - 2 + i;
                                        }
                                        return (_jsx(PageButton, { "$active": page === pageNum, onClick: () => setPage(pageNum), children: pageNum }, pageNum));
                                    }), _jsx(PageButton, { disabled: page >= totalPages, onClick: () => setPage(p => Math.min(totalPages, p + 1)), children: _jsx(FaChevronRight, {}) })] })] })] })) : (_jsxs(EmptyState, { children: [_jsx(FaHistory, {}), _jsx("h3", { children: "No Auction History" }), _jsx("p", { children: dateFrom || dateTo || resultFilter
                            ? 'No auctions match the current filters. Try adjusting your date range or result filter.'
                            : 'Completed and cancelled auctions will appear here.' })] })), selectedAuction && (_jsx(DetailOverlay, { onClick: () => setSelectedAuction(null), children: _jsxs(DetailModal, { onClick: e => e.stopPropagation(), children: [_jsxs(DetailHeader, { children: [_jsxs("div", { children: [_jsxs("h3", { children: [_jsx(FaGavel, { style: { marginRight: '0.5rem', color: 'var(--accent-gold)' } }), "Auction Details"] }), _jsxs("div", { style: { fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }, children: [selectedAuction.diamondSnapshot.caratWeight, "ct", ' ', selectedAuction.diamondSnapshot.shape, ' ', selectedAuction.diamondSnapshot.color, ' ', selectedAuction.diamondSnapshot.clarity] })] }), _jsx(CloseButton, { onClick: () => setSelectedAuction(null), children: "\u00D7" })] }), _jsxs(DetailGrid, { children: [_jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Auction Type" }), _jsx(DetailValue, { style: { textTransform: 'capitalize' }, children: selectedAuction.auctionType })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Visibility" }), _jsx(DetailValue, { style: { textTransform: 'capitalize' }, children: selectedAuction.visibility })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Starting Price" }), _jsxs(DetailValue, { children: ["$", selectedAuction.startingPrice.toLocaleString()] })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Final Price" }), _jsx(DetailValue, { style: { color: 'var(--accent-gold)' }, children: selectedAuction.finalPrice
                                                ? `$${selectedAuction.finalPrice.toLocaleString()}`
                                                : 'N/A' })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Reserve Price" }), _jsx(DetailValue, { children: selectedAuction.reservePrice
                                                ? `$${selectedAuction.reservePrice.toLocaleString()}`
                                                : 'No Reserve' })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Min Increment" }), _jsxs(DetailValue, { children: [selectedAuction.minimumIncrementPercent, "%"] })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Total Bids" }), _jsx(DetailValue, { children: selectedAuction.bidCount })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Result" }), _jsx(DetailValue, { children: _jsxs(ResultBadge, { "$result": getResult(selectedAuction), children: [getResultIcon(getResult(selectedAuction)), ' ', getResultLabel(getResult(selectedAuction))] }) })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Scheduled Start" }), _jsx(DetailValue, { style: { fontSize: '0.85rem' }, children: new Date(selectedAuction.scheduledStartTime).toLocaleString() })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Ended" }), _jsx(DetailValue, { style: { fontSize: '0.85rem' }, children: selectedAuction.actualEndTime
                                                ? new Date(selectedAuction.actualEndTime).toLocaleString()
                                                : new Date(selectedAuction.scheduledEndTime).toLocaleString() })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Duration" }), _jsx(DetailValue, { children: formatDuration(selectedAuction) })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Grace Extensions" }), _jsxs(DetailValue, { children: [selectedAuction.graceExtensionCount, " / ", selectedAuction.maxGraceExtensions] })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Stock Number" }), _jsx(DetailValue, { children: selectedAuction.diamondStockNumber || '-' })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Certificate" }), _jsxs(DetailValue, { children: [selectedAuction.diamondSnapshot.lab, " ", selectedAuction.diamondSnapshot.certificateNumber || '-'] })] })] })] }) }))] }));
};
export default AuctionHistoryTab;
